import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse, HttpClient } from '@angular/common/http';
import {catchError, Observable, switchMap, throwError, tap, BehaviorSubject, filter, take, mergeMap} from 'rxjs';
import {AppNotificationService} from "../services/notification.service";
import { environment } from 'src/environments/environment';
import { Route, Router } from '@angular/router';
import { TokenStorageService } from 'src/app/authentication/storage/token-storage.service';
import { TokenService } from 'src/app/authentication/services/token.service';
import { translate } from '@jsverse/transloco';

const TOKEN_HEADER_KEY = 'Authorization';

@Injectable()
export class ErrorCatchingInterceptor implements HttpInterceptor {

  private isRefreshing = false;
  private refreshQueue = [];

  constructor(
    private appnotificationService: AppNotificationService,
    private router: Router,
    private tokenStoreService: TokenStorageService,
    ) {
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.log("error in Error Interceptor", error ?? 'No Error');
          let errorMsg = '';
          const isLoginRequest = request.method === 'POST' && request.url.endsWith('/tokens');
          if (isLoginRequest && (error.status === 401 || error.status === 400)) {
            this.appnotificationService.notification$.next({
              message: translate('global.errors.login.invalidCredentials'),
              style: 'error',
            });
            return throwError(() => `Error Code: ${error.status},  Message: ${error.message}`);
          }
          if(error.status === 401){
            return this.handleTokenExpired(request, next, error);
          }
          if (error.error instanceof ErrorEvent) {
            console.log(error, "error of type ErrorEvent");
            this.appnotificationService.notification$.next({message: translate(error.error.message), style: 'error'})
            errorMsg = `Error: ${error.error.message}`;
          } 
          //TODO: streamline all error Messages, best case would be translated errors from the backend
          else if(error.error?.errors?.ConfirmNewPassword){
            this.appnotificationService.notification$.next({message: translate(error.error.errors.ConfirmNewPassword[0]), style: 'error'})
            errorMsg = `Error Code: ${error.status},  Message: ${error.message}`;
          }
          else if(error.error?.errors?.EquipmentNumber){
            this.appnotificationService.notification$.next({message: translate("global."+ error.error.errors.EquipmentNumber[0]), style: 'error'}) // add "global." prefix
            errorMsg = `Error Code: ${error.status},  Message: ${error.message}`;
          }
          else if(error.error?.errors?.Name){
            this.appnotificationService.notification$.next({message: translate(error.error.errors.Name[0]), style: 'error'})
            errorMsg = `Error Code: ${error.status},  Message: ${error.message}`;
          }
          else if(error.error?.errors?.Index){
            this.appnotificationService.notification$.next({message: translate(error.error.errors.Index[0]), style: 'error'})
            errorMsg = `Error Code: ${error.status},  Message: ${error.message}`;
          }
          else if(error.error?.errors?.IsFinalStatus){
            this.appnotificationService.notification$.next({message: translate(error.error.errors.IsFinalStatus[0]), style: 'error'})
            errorMsg = `Error Code: ${error.status},  Message: ${error.message}`;
          }
          else if(error.error?.errors?.InternalNumber){
            this.appnotificationService.notification$.next({message: translate(error.error.errors.InternalNumber[0]), style: 'error'})
            errorMsg = `Error Code: ${error.status},  Message: ${error.message}`;
          }
          else if(error.error?.errors?.TenantId){
            this.appnotificationService.notification$.next({message: translate(error.error.errors.TenantId[0]), style: 'error'})
            errorMsg = `Error Code: ${error.status},  Message: ${error.message}`;
          }
          else if(this.firstInventoryValidationMessage(error)){
            this.appnotificationService.notification$.next({message: translate(this.firstInventoryValidationMessage(error)), style: 'error'})
            errorMsg = `Error Code: ${error.status},  Message: ${error.message}`;
          }
          else if(error.error?.messages){
            var errorMessages = error.error.messages;
            errorMessages.forEach((message) => {
              console.log(`${message}`);
              this.appnotificationService.notification$.next({message: translate(message), style: 'error'})
              errorMsg = `Error Code: ${error.status},  Message: ${message}`;
            });
          }
          else {
            console.log(error, "general Error");
            this.appnotificationService.notification$.next({message: translate('global.errors.general'), style: 'error'})
            errorMsg = `Error Code: ${error.status},  Message: ${error.message}`;
          }
          console.log(errorMsg);
          return throwError(() => errorMsg);
        })
      )
  }

  private handleTokenExpired(request: HttpRequest<any>, next: HttpHandler,error: HttpErrorResponse): Observable<HttpEvent<any>> {
    // Call the refresh token endpoint to get a new access token
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      return this.tokenStoreService.refreshAccessToken().pipe(
        switchMap((token : string) => {
          // Retry the original request with the new access token
          this.isRefreshing = false;
          this.refreshQueue.forEach((req) => {
            const newRequest = this.addTokenHeader(req.request, token);
            req.next.handle(newRequest).subscribe({
              next: (res) => req.subscriber.next(res),
              error: (err) => req.subscriber.error(err),
              complete: () => req.subscriber.complete()
            });
          });

          this.refreshQueue = [];
          const newRequest = this.addTokenHeader(request, token);
          return next.handle(newRequest);
        }),
        catchError((error) => {
          // Handle refresh token error (e.g., redirect to login page)
          console.error('Error handling expired access token:', error);
          this.isRefreshing = false;
          this.tokenStoreService.signOut();
          this.refreshQueue.forEach((req) => req.subscriber.error(error));
          this.refreshQueue = [];
          this.router.navigate(['/auth']);
          return throwError(error);
        })
      );
    } else {
        return new Observable<HttpEvent<any>>((subscriber) => {
            this.refreshQueue.push({ request, next, subscriber, error });
        });
    }
    
  }

  // Validation messages of the inventory module are sent as translation keys (global.errors.inventoryDevice.*)
  private firstInventoryValidationMessage(error: HttpErrorResponse): string | null {
    const errors = error.error?.errors;
    if (!errors || typeof errors !== 'object') {
      return null;
    }

    for (const field of Object.keys(errors)) {
      const message = Array.isArray(errors[field]) ? errors[field][0] : null;
      if (typeof message === 'string' && message.startsWith('global.errors.inventoryDevice')) {
        return message;
      }
    }

    return null;
  }

  private addTokenHeader(request: HttpRequest<any>, token: string) {
    return request.clone(
      { 
        headers: request.headers.set(TOKEN_HEADER_KEY, 'Bearer ' + token) 
      });
  }
}
